import { useEffect, useRef, useState } from "react";
import { api, formatApiError, fmtDate, downloadPdf } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileDown, Trash2, Pencil, Loader2, Paperclip, Save, X, Upload } from "lucide-react";
import { ExpiryBadge } from "@/components/ExpiryBadge";
import FileUploader from "@/components/FileUploader";
import { stagedName } from "@/lib/stagedFiles";
import { toast } from "sonner";

const EMPTY = { check_date: "", checked_by: "", licence_number: "", licence_categories: "", points: "", check_code: "", licence_expiry: "", result: "ok", notes: "" };

function CheckFields({ form, set, prefix }) {
  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={`${prefix}-${k}`} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 rounded-sm border-slate-300" {...props} />
    </div>
  );
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {field("check_date", "Check date", { type: "date" })}
        {field("checked_by", "Checked by", { placeholder: "Defaults to you" })}
        {field("licence_number", "Licence number")}
        {field("licence_categories", "Categories (e.g. B, C1, C)")}
        {field("points", "Penalty points", { type: "number", min: 0 })}
        {field("licence_expiry", "Licence expiry", { type: "date" })}
        {field("check_code", "DVLA check code (share code)")}
        <div>
          <Label className="text-xs text-slate-600">Result</Label>
          <Select value={form.result || "ok"} onValueChange={(v) => set("result", v)}>
            <SelectTrigger data-testid={`${prefix}-result`} className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ok">OK to drive</SelectItem>
              <SelectItem value="issue">Issue found</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4">
        <Label className="text-xs text-slate-600">Notes</Label>
        <Textarea data-testid={`${prefix}-notes`} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={3} className="mt-1 rounded-sm border-slate-300" />
      </div>
    </>
  );
}

export default function LicenceChecks({ driver, onChange }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/hr/drivers/${driver.id}/checks`).then((r) => setRows(r.data || [])).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [driver.id]); // eslint-disable-line
  const refresh = () => { load(); onChange(); };

  const remove = async (c) => {
    if (!window.confirm(`Delete the licence check from ${fmtDate(c.check_date)}?`)) return;
    try { await api.delete(`/hr/checks/${c.id}`); toast.success("Check deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <NewCheckCard driver={driver} onSaved={refresh} />
      <Card className="rounded-sm border border-slate-200 shadow-none" data-testid="hr-checks-card">
        <div className="p-5 border-b border-slate-100">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Licence check history</div>
          <p className="text-[11px] text-slate-500 mt-1">Every saved check is kept here. Download a PDF of any check.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full po-table text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider">
                <th className="px-3 py-3 font-semibold">Check date</th>
                <th className="px-3 py-3 font-semibold">Checked by</th>
                <th className="px-3 py-3 font-semibold text-center">Points</th>
                <th className="px-3 py-3 font-semibold">Categories</th>
                <th className="px-3 py-3 font-semibold">Result</th>
                <th className="px-3 py-3 font-semibold">Next due</th>
                <th className="px-3 py-3 font-semibold">DVLA summary</th>
                <th className="px-3 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500" data-testid="hr-checks-empty">No licence checks recorded yet.</td></tr>}
              {rows.map((c) => <CheckRow key={c.id} c={c} driver={driver} onEdit={() => setEditing(c)} onDelete={() => remove(c)} />)}
            </tbody>
          </table>
        </div>
        <EditCheckDialog driver={driver} check={editing} onClose={() => setEditing(null)} onSaved={refresh} />
      </Card>
    </>
  );
}

function NewCheckCard({ driver, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const addFiles = (list) => setFiles((f) => [...f, ...Array.from(list || []).map((file) => ({ file, name: file.name }))]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.check_date) { toast.error("Enter the check date"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/hr/drivers/${driver.id}/checks`, { ...form, points: Number(form.points) || 0 });
      let failed = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file, stagedName(f));
        try {
          await api.post(`/uploads?entity_type=hr_check&entity_id=${r.data.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch { failed += 1; }
      }
      if (failed) toast.warning(`Check saved, but ${failed} file${failed === 1 ? "" : "s"} failed to upload — attach via the edit button`);
      else toast.success("Check saved to history");
      setForm(EMPTY); setFiles([]);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="rounded-sm border border-slate-200 shadow-none p-5" data-testid="hr-new-check-card">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Driving licence check</div>
      <div className="flex flex-wrap gap-x-10 gap-y-3 mb-5 pb-5 border-b border-slate-100" data-testid="hr-licence-status">
        <div><div className="text-[11px] text-slate-500">Last check</div><div className="text-sm font-medium text-slate-900 mt-1" data-testid="hr-last-check-date">{driver.last_check_date ? fmtDate(driver.last_check_date) : "No checks yet"}</div></div>
        <div><div className="text-[11px] text-slate-500">Next check due</div><div className="mt-1"><ExpiryBadge date={driver.next_check_due} testid="hr-next-check-status" /></div></div>
        <div><div className="text-[11px] text-slate-500">Licence expiry</div><div className="mt-1"><ExpiryBadge date={driver.licence_expiry} testid="hr-licence-expiry-status" /></div></div>
        <div><div className="text-[11px] text-slate-500">Current points</div><div className="text-sm font-medium text-slate-900 mt-1">{driver.licence_points || 0}</div></div>
        <div><div className="text-[11px] text-slate-500">Licence number</div><div className="text-sm font-mono text-slate-900 mt-1">{driver.licence_number || "—"}</div></div>
      </div>
      <CheckFields form={form} set={set} prefix="hr-check" />
      <div className="mt-4">
        <Label className="text-xs text-slate-600">DVLA summary (PDF or photo)</Label>
        <div data-testid="hr-check-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          className={`mt-1 flex flex-wrap items-center gap-2 rounded-sm border-2 border-dashed p-3 transition-colors ${drag ? "border-slate-900 bg-slate-100" : "border-slate-200 bg-slate-50/50"}`}>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => inputRef.current?.click()} data-testid="hr-check-attach-btn">
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Attach file
          </Button>
          <span className="text-xs text-slate-500">or drag &amp; drop files here</span>
          <input ref={inputRef} type="file" multiple accept="image/*,.pdf" className="hidden" data-testid="hr-check-attach-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          {files.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-sm pl-2 pr-1 py-0.5 bg-slate-50" data-testid={`hr-check-staged-${i}`}>
              <Paperclip className="h-3 w-3 shrink-0" />
              <input value={f.name} title="Name this file" data-testid={`hr-check-staged-name-${i}`}
                onChange={(e) => setFiles((fs) => fs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                className="w-44 bg-transparent outline-none border-b border-dashed border-slate-300 focus:border-slate-600" />
              <button type="button" onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      </div>
      <div className="mt-5 flex items-center gap-3">
        <Button data-testid="hr-check-save" className="rounded-sm bg-slate-900 hover:bg-slate-800" disabled={saving} onClick={save}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save check
        </Button>
        <span className="text-[11px] text-slate-500">Saves to the history below and clears the form. Next check is set 3 months after the check date.</span>
      </div>
    </Card>
  );
}

function CheckRow({ c, driver, onEdit, onDelete }) {
  const ok = c.result !== "issue";
  const pdfName = `licence-check-${(driver.name || "driver").replace(/[^A-Za-z0-9]+/g, "-")}-${c.check_date}.pdf`;
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`hr-check-row-${c.id}`}>
      <td className="px-3 py-3 font-medium text-slate-900">{fmtDate(c.check_date)}</td>
      <td className="px-3 py-3 text-slate-700">{c.checked_by || "—"}</td>
      <td className="px-3 py-3 text-center tabular-nums">{c.points || 0}</td>
      <td className="px-3 py-3 text-slate-700 text-xs">{c.licence_categories || "—"}</td>
      <td className="px-3 py-3">
        <span className={`text-[10px] uppercase tracking-wider font-semibold border rounded-sm px-1.5 py-0.5 ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-300 bg-red-50 text-red-700"}`}>{ok ? "OK" : "Issue"}</span>
      </td>
      <td className="px-3 py-3 text-slate-700 text-xs">{fmtDate(c.next_check_due)}</td>
      <td className="px-3 py-3 text-xs text-slate-600"><Paperclip className="h-3 w-3 inline mr-1" />{c.file_count || 0}</td>
      <td className="px-3 py-3 text-right whitespace-nowrap">
        <Button data-testid={`hr-check-pdf-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" title="Download PDF" onClick={() => downloadPdf(`/hr/checks/${c.id}/pdf`, pdfName)}><FileDown className="h-4 w-4" /></Button>
        <Button data-testid={`hr-check-edit-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" title="Edit / attach DVLA summary" onClick={onEdit}><Pencil className="h-4 w-4" /></Button>
        <Button data-testid={`hr-check-delete-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600" onClick={onDelete}><Trash2 className="h-4 w-4" /></Button>
      </td>
    </tr>
  );
}

function EditCheckDialog({ driver, check, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (check) setForm({ ...check }); }, [check]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/hr/checks/${check.id}`, { ...form, points: Number(form.points) || 0 });
      toast.success("Check updated");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!check} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hr-check-dialog">
        <DialogHeader><DialogTitle>Edit licence check · {driver.name}</DialogTitle></DialogHeader>
        <CheckFields form={form} set={set} prefix="hr-edit-check" />
        {check && (
          <div className="border-t border-slate-100 pt-4">
            <FileUploader entityType="hr_check" entityId={check.id} label="DVLA summary (PDF or photo)" accept="image/*,.pdf" showDocNumber onChange={onSaved} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="hr-edit-check-close">Close</Button>
          <Button data-testid="hr-edit-check-save" className="rounded-sm bg-slate-900 hover:bg-slate-800" disabled={saving || !form.check_date} onClick={save}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
